import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, throwError } from 'rxjs';
import { ErrorContextService } from '../../infrastructure/services/error-context/error-context.service';
import { API_CONFIG } from '../../config/api.config';

/**
 * URLs que deben quedar excluidas del interceptor de errores.
 * Agregar aquí los endpoints que manejan sus propios errores.
 */
const EXCLUDED_URLS: string[] = [
  API_CONFIG.ENDPOINTS.CREAR_COTIZACION_SALUD_ANIMAL,
];

/**
 * Interceptor funcional de manejo global de errores HTTP.
 *
 * - Errores 5xx → redirige a /error-500 y corta el flujo del observable
 * - Errores 4xx (excepto 404) → guarda contexto y redirige a /error-general
 * - Error 404 → propaga para que sea manejado por rutas
 * - Otros errores → los propaga para que cada servicio/componente los maneje
 * - Permite excluir URLs específicas con el header 'X-Skip-Error-Interceptor'
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const errorContextService = inject(ErrorContextService);

  const debeExcluir =
    req.headers.has('X-Skip-Error-Interceptor') ||
    EXCLUDED_URLS.some((url) => req.url.includes(url));

  if (debeExcluir) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Errores 5xx → error-500
      if (error.status >= 500) {
        console.error('[ErrorInterceptor] Error de servidor:', {
          url: req.url,
          method: req.method,
          status: error.status,
          mensaje: error.message,
          timestamp: new Date().toISOString(),
        });

        router.navigate(['/error-500']);
        return EMPTY;
      }

      // Error 404 → propagar para que sea manejado por rutas
      if (error.status === 404) {
        return throwError(() => error);
      }

      // Otros errores 4xx → error con contexto
      if (error.status >= 400 && error.status < 500) {
        console.error('[ErrorInterceptor] Error de cliente:', {
          url: req.url,
          method: req.method,
          status: error.status,
          mensaje: error.message,
          timestamp: new Date().toISOString(),
        });

        // Guardar contexto para permitir reintento
        errorContextService.saveContext(router.url, error.status, error.message, req.url);

        router.navigate(['/error']);
        return EMPTY;
      }

      return throwError(() => error);
    }),
  );
};
